import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import Button from '../components/Button';
import StoreBadges from '../components/StoreBadges';
import './PublicWebinarRegister.css';

const API = window.location.origin + '/api';
const DL_URL = 'https://prozpects.com/dl';

export default function PublicWebinarRegisterPage() {
  const { slug } = useParams();
  const [webinar, setWebinar] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Flow: 'form' -> 'done' (registration complete; free path). Paid path redirects to Stripe.
  const [stage, setStage] = useState('form');
  const [publicToken, setPublicToken] = useState('');
  const [code, setCode] = useState('');
  const [verifyMsg, setVerifyMsg] = useState('');
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    axios.get(`${API}/webinars/public/${slug}`)
      .then((r) => setWebinar(r.data))
      .catch(() => setLoadError('This webinar is not available.'));
  }, [slug]);

  const fmtWhen = (iso, tz) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
        timeZone: tz || undefined, timeZoneName: 'short',
      });
    } catch { return iso; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/webinars/public/${slug}/register`, form);
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url; // paid path
        return;
      }
      setPublicToken(r.data.public_token);
      setStage('done');
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'already_registered') setError("You're already registered with that email.");
      else if (detail?.code === 'invalid_phone') setError('Please enter a valid mobile number.');
      else setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setVerifyMsg('');
    try {
      await axios.post(`${API}/webinars/public/${slug}/verify-phone`,
        { public_token: publicToken, code });
      setVerified(true);
    } catch (err) {
      const c = err.response?.data?.detail?.code;
      if (c === 'code_expired') setVerifyMsg('That code expired. Tap "Resend code".');
      else if (c === 'too_many_attempts') setVerifyMsg('Too many tries. Tap "Resend code" for a new one.');
      else setVerifyMsg("That code didn't match. Try again.");
    }
  };

  const resend = async () => {
    setVerifyMsg('');
    try {
      await axios.post(`${API}/webinars/public/${slug}/resend-code`, { public_token: publicToken });
      setVerifyMsg('New code sent.');
    } catch (err) {
      if (err.response?.status === 429) setVerifyMsg('Please wait a minute before resending.');
      else setVerifyMsg('Could not resend right now.');
    }
  };

  if (loadError) {
    return (
      <div className="pwr-shell"><div className="pwr-card">
        <img src="/logo-wordmark.png" alt="ProZpects" className="pwr-logo" />
        <p>{loadError}</p>
      </div></div>
    );
  }
  if (!webinar) {
    return (
      <div className="pwr-shell"><div className="pwr-card">
        <img src="/logo-wordmark.png" alt="ProZpects" className="pwr-logo" />
        <p>Loading…</p>
      </div></div>
    );
  }

  // ── Registration complete (free path) ──────────────────────────────
  if (stage === 'done') {
    return (
      <div className="pwr-shell">
        <div className="pwr-card">
          <img src="/logo-wordmark.png" alt="ProZpects" className="pwr-logo" />
          <header className="pwr-hero">
            <h1>You're registered! 🎉</h1>
            <p><strong>{webinar.title}</strong></p>
            <p>Check your email for the join link and calendar invite.</p>
          </header>

          <div className="pwr-verified">
            <div className="pwr-dl-heading">Download the ProZpects App</div>
            <div className="pwr-qr">
              <QRCodeSVG value={DL_URL} size={188} level="H"
                imageSettings={{ src: '/logo.png', height: 44, width: 44, excavate: true }} />
            </div>
            <StoreBadges className="pwr-badges" />
            <p className="pwr-note">
              Scan the QR code or tap your app store above — or visit{' '}
              <a href={DL_URL} target="_blank" rel="noopener noreferrer" className="pwr-dl-link">prozpects.com/dl</a>
            </p>
          </div>

          <div className="pwr-app">
            <div className="pwr-section">Webinar reminders</div>
            {!verified ? (
              <>
                <p className="pwr-note">We texted a 6-digit code to {form.phone}. Enter it to get SMS reminders before the webinar (optional).</p>
                <form onSubmit={verify} className="pwr-verify">
                  <input inputMode="numeric" maxLength={6} placeholder="Code" value={code}
                    onChange={(e) => setCode(e.target.value)} />
                  <Button type="submit" variant="primary" size="sm">Verify</Button>
                </form>
                <button type="button" className="pwr-link" onClick={resend}>Resend code</button>
                {verifyMsg && <p className="pwr-note">{verifyMsg}</p>}
              </>
            ) : (
              <p className="pwr-verified-msg" style={{ textAlign: 'left' }}>✓ Reminders on — we'll text you before the webinar.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────────
  return (
    <div className="pwr-shell">
      <div className="pwr-card">
        <img src="/logo-wordmark.png" alt="ProZpects" className="pwr-logo" />
        <header className="pwr-hero">
          <h1>{webinar.title}</h1>
          <p className="pwr-hero__meta"><strong>{fmtWhen(webinar.start_at, webinar.timezone)}</strong></p>
          {webinar.description && <p>{webinar.description}</p>}
          {webinar.price_cents > 0 && (
            <p className="pwr-price">${(webinar.price_cents / 100).toFixed(2)}</p>
          )}
        </header>

        {error && <div className="pwr-error" role="alert">{error}</div>}

        <form onSubmit={submit} className="pwr-form">
          <div className="pwr-section">Register</div>
          <div className="pwr-row">
            <label>First name
              <input required value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            </label>
            <label>Last name
              <input required value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </label>
          </div>
          <label>Email
            <input required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label>Mobile phone
            <input required type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <p className="pwr-consent">
            By registering you agree to receive a verification code and webinar reminders by text.
            Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help.
          </p>
          <Button type="submit" variant="primary" fullWidth className="pwr-submit" disabled={submitting}>
            {submitting ? 'Registering…' : (webinar.price_cents > 0 ? 'Register & Pay' : 'Register')}
          </Button>
        </form>

        <div className="pwr-app">
          <div className="pwr-section">Get the App</div>
          <p className="pwr-note">
            Message coaches, build your trading card, and add photos &amp; video on the ProZpects app.
          </p>
          <StoreBadges className="pwr-badges" />
        </div>
      </div>
    </div>
  );
}
